import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Select, MenuItem, FormControl } from '@mui/material';
import { Bar, Doughnut } from 'react-chartjs-2';
import AddProviderIcon from '../../assets/icons/dashbord-icons/Add-provider.svg';
import verify from '../../assets/icons/dashbord-icons/chekedverify.svg';
import newlyAdded from '../../assets/icons/dashbord-icons/newlyJoin.svg';
import offBoarded from '../../assets/icons/dashbord-icons/off-board.svg';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import CommonBreadcrumbs from '../common-breadcrumbs/CommonBreadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { loadDiscrepancyData, loadMetricsData, loadPayerCycleMetricsData } from '../../redux/reducers/dashboardSlice';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);
ChartJS.register(ChartDataLabels);

const Dashboard = () => {
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState('1');
  const [selectedCycle, setSelectedCycle] = useState('1');
  const BreadcrumbsPayers = [
    { label: 'Dashboard', },
  ];

  const metricsData = useSelector((state) => state.dashboard.metricsData);
  const payerCycleMetricsData = useSelector((state) => state.dashboard.payerCycleMetricsData);
  const discrepanieseData = useSelector((state) => state.dashboard.discrepancyData);

  useEffect(() => {
    // Dispatch the thunks to load data
    dispatch(loadMetricsData({ metricsRange: selectedValue }));
    dispatch(loadPayerCycleMetricsData({ metricsRange: selectedCycle }));
    dispatch(loadDiscrepancyData());
  }, [selectedValue, selectedCycle]);


  // Handler for changing the selected value
  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };
  const handleCycleChange = (event) => {
    setSelectedCycle(event.target.value);
  };

  const providerPayerData = {
    labels: metricsData?.response_data?.plan_list?.map((plan) => plan.plan_name) || [],
    datasets: [
      {
        label: 'Terminated',
        backgroundColor: '#EA8181',
        data: metricsData?.response_data?.plan_list?.map((plan) => plan.termination) || [],
        stack: 'stack1',
      },
      {
        label: 'Changes',
        backgroundColor: '#d3d3d3',
        data: metricsData?.response_data?.plan_list?.map((plan) => plan.changes) || [],
        stack: 'stack1',
      },
      {
        label: 'Addition',
        backgroundColor: '#C6E4F0',
        data: metricsData?.response_data?.plan_list?.map((plan) => plan.additions) || [],
        stack: 'stack1',
      },
    ],
  };


  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (context) => `${context.dataset.label}: ${context.raw}` },
        backgroundColor: '#000',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
      },
      datalabels: {
        display: (context) => {
          // Display label only for the top dataset in the stack
          const datasetIndex = context.datasetIndex;
          const datasets = context.chart.data.datasets;
          return datasetIndex === datasets.length - 1;
        },
        color: '#000',
        anchor: 'end',
        align: 'end',
        offset: -5,
        font: {
          size: 12,
          weight: '400',
        },
        formatter: (value) => value,
      },
    },
    barPercentage: 0.5,
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { display: false },
        grid: { display: false },
      },
    },
    elements: {
      bar: {
        barThickness: 20,
        borderRadius: 6,
      },
    },
  };



  // New data for "Time for Payers to Respond" chart
  const payerResponseData = {
    labels: payerCycleMetricsData?.response_data?.map((plan) => plan.plan_name) || [],
    datasets: [
      {
        label: 'Response Time',
        backgroundColor: '#CCE9F6',
        data: payerCycleMetricsData?.response_data?.map((plan) => plan.response_time) || [],
      },
    ],
  };


  // const payerResponseOptions = {
  //   responsive: true,
  //   plugins: {
  //     legend: { display: false },
  //     tooltip: {
  //       callbacks: { label: (context) => `${context.dataset.label}: ${context.raw} days` },
  //       backgroundColor: '#000',
  //       titleColor: '#fff',
  //       bodyColor: '#fff',
  //       padding: 10,
  //     },
  //     datalabels: {
  //       display: false,
  //     },
  //   },
  //   barPercentage: 0.6,
  //   categoryPercentage: 1,
  //   scales: {
  //     x: {
  //       grid: { display: false },
  //       // categoryPercentage: 1,
  //       // barPercentage: 1,
  //       border: {
  //         display: true,
  //         width: 1,
  //         color: '#969696',
  //       }
  //     },
  //     y: {
  //       beginAtZero: true,
  //       grid: { display: true, color: '#e0e0e0' },
  //       border: {
  //         display: true,
  //         dash: [2, 5],
  //         width: 1,
  //         color: '#969696',
  //       },
  //       ticks: { display: true },
  //       title: {
  //         display: true,
  //         text: 'Days',
  //         color: '#344049',
  //         font: {
  //           size: 14,
  //           weight: '500',
  //         },
  //       },
  //     },
  //   },
  //   elements: {
  //     bar: {
  //       borderRadius: 6,
  //       //barThickness: 40,
  //     },
  //   },
  // };


  const payerResponseOptions = {
    responsive: true,
    maintainAspectRatio: false, // Ensure the chart fills the container height
    layout: {
      padding: {
        left: 0, 
        right: 0,
        top: 10,
        bottom: 10, 
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (context) => `${context.dataset.label}: ${context.raw} days` },
        backgroundColor: '#000',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
      },
      datalabels: {
        display: false,
      },
    },
    barPercentage: 0.4, // Adjusted to reduce gaps between bars
    categoryPercentage: 0.8, // Adjusted to reduce gaps between categories
    scales: {
      x: {
        grid: { display: false },
        border: {
          display: true,
          width: 1,
          color: '#969696',
        },
        ticks: {
          padding: 5, // Reduce padding between x-axis labels and the chart
        },
      },
      y: {
        beginAtZero: true,
        grid: { display: true, color: '#e0e0e0' },
        border: {
          display: true,
          dash: [2, 5],
          width: 1,
          color: '#969696',
        },
        ticks: {
          display: true,
          padding: 5, // Reduce padding between y-axis ticks and the chart
        },
        title: {
          display: true,
          text: 'Days',
          color: '#344049',
          font: {
            size: 14,
            weight: '500',
          },
          padding: 5, // Reduce padding around the y-axis title
        },
      },
    },
    elements: {
      bar: {
        borderRadius: 6,
        // Removed barThickness to allow dynamic scaling
      },
    },
  };

  ChartJS.register({
    id: 'centerText',
    beforeDraw: function (chart) {
      if (chart.config.options.plugins.centerText) {
        const { width, height, ctx } = chart;
        const text = chart.config.options.plugins.centerText.text;
        const fontSize = chart.config.options.plugins.centerText.fontSize || '20px';
        const color = chart.config.options.plugins.centerText.color || '#000';
        const fontWeight = chart.config.options.plugins.centerText.fontWeight || 'normal';

        ctx.save();
        ctx.font = `${fontWeight} ${fontSize} Arial`;
        ctx.fillStyle = color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, width / 2, height / 2);
        ctx.restore();
      }
    },
  });


  const discrepancyData = {
    labels: discrepanieseData?.response_data?.payer_error?.map((plan) => plan.plan_name) || [],
    datasets: [
      {
        label: 'Data Discrepancies',
        data: discrepanieseData?.response_data?.payer_error?.map((plan) => plan.errors) || [],
        backgroundColor: [
          '#E27676', '#EA8787', '#ED9A9A', '#F0A7A7', '#F3C0C0', '#F5CDCD', '#FFDADA'
        ],
        hoverBackgroundColor: [
          '#E27676', '#EA8787', '#ED9A9A', '#F0A7A7', '#F3C0C0', '#F5CDCD', '#FFDADA'
        ],
        borderWidth: 0,
      },
    ],
  };


  const discrepancyOptions = {
    responsive: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw}`,
        },
        backgroundColor: '#000',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
      },
      centerText: {
        display: true,
        text: 'Payers',
        color: '#344049',
        fontSize: '17px',
        fontWeight: '400',
      },
      datalabels: {
        display: true,
        color: '#344049',
        font: {
          size: 12,
          weight: '400',
        },
        anchor: 'end',
        align: 'end',
        offset: 2,
        formatter: (value, context) => {
          const label = context.chart.data.labels[context.dataIndex];
          return `${label}\n${value}`;
        },
      },
    },
    layout: {
      padding: 30,
    },
    cutout: '70%',
  };



  // Data and options for the Time to Resolve Discrepancies (Bar Chart)
  const timeToResolveData = {
    labels: discrepanieseData?.response_data?.error_response?.map((plan) => plan.plan_name) || [],
    datasets: [
      {
        label: 'Resolution Time (Days)',
        data: discrepanieseData?.response_data?.error_response?.map((plan) => plan.time_resolve_errors) || [],
        backgroundColor: '#CCE9F6',
      },
    ],
  };


  const timeToResolveOptions = {
    responsive: true,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}`,
        },
        backgroundColor: '#000',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
      },
      datalabels: {
        display: false, // Explicitly disable data labels for this chart
      },
      title: {
        display: true,
        text: 'Days',
        color: '#344049',
        font: {
          size: 14,
          weight: '500',
        },
        position: 'bottom', // Position at the bottom of the chart
        align: 'center', // Center align the text
        padding: {
          top: 10, // Add padding to create space between the label and the axis
        },
      },
    },
    barPercentage: 0.7,
    scales: {
      x: {
        beginAtZero: true,
        border: {
          display: true,
          dash: [2, 5],
          width: 1,
          color: '#969696',
        },
        grid: { color: '#e0e0e0', },
        ticks: { display: true, borderColor: '#000', callback: (value) => `${value}` },
      },
      y: {
        border: {
          width: 1,
          color: '#969696',
        },
        grid: { display: false, color: '#000', },
        ticks: { font: { size: 12, weight: '500' }, color: '#666' }, // Darker labels on y-axis
      },
    },
    elements: {
      bar: {
        backgroundColor: '#CCE9F6', // Light blue color for bars
      },
    },
  };



  const verifyImage = new Image();
  verifyImage.src = verify;
  ChartJS.register({
    id: 'checkMarks',
    afterDatasetsDraw(chart) {
      if (chart.config.options.plugins.checkMarks && chart.config.options.plugins.checkMarks.display) {
        const { ctx, data } = chart;
        data.datasets[0].data.forEach((value, index) => {
          const meta = chart.getDatasetMeta(0);
          const barPosition = meta.data[index].tooltipPosition();
          const imageSize = 20;
          ctx.drawImage(verifyImage, barPosition.x - imageSize / 2, barPosition.y - imageSize - 5, imageSize, imageSize);
        });
      }
    },
  });

  const cycleCloseDurationData = {
    labels: payerCycleMetricsData?.response_data?.map((plan) => plan.plan_name) || [],
    datasets: [
      {
        label: 'Avg. Cycle Close Duration',
        data: payerCycleMetricsData?.response_data?.map((plan) => plan.cycle_duration) || [],
        backgroundColor: '#CCE9F6',
      },
    ],
  };


  const cycleCloseDurationOptions = {
    responsive: true,
    maintainAspectRatio: false, 
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (context) => `${context.dataset.label}: ${context.raw}` },
        backgroundColor: '#000',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
      },
      // Enable check marks plugin specifically for this chart
      checkMarks: {
        display: true,
      },
      datalabels: {
        display: false, // Explicitly disable data labels for this chart
      },
    },
    barPercentage: 0.4,
    categoryPercentage: 0.8, 
    scales: {
      x: {
        grid: { display: false },
        border: {
          width: 1,
          color: '#969696',
        },
      },
      y: {
        beginAtZero: true,
        grid: { display: true, color: '#e0e0e0', borderDash: [5, 5] },
        ticks: { callback: (value) => `${value}` },
        border: {
          display: true,
          dash: [2, 5],
          width: 1,
          color: '#969696',
        },
        title: {
          display: true,
          text: 'Days',
          color: '#344049',
          font: {
            size: 14,
            weight: '500',
          },
        },
      },
    },
    elements: {
      bar: {
        borderRadius: 6,
      },
    },
  };

  return (
    <Box sx={{ backgroundColor: '#F8F8F8', py: 2, px: 2 }}>
      <CommonBreadcrumbs breadcrumbs={BreadcrumbsPayers} />
      {/* Header with Select */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>Provider & Payer Metrics</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" mr={1}>Select period</Typography>
          <FormControl variant="outlined" sx={{ minWidth: 150 }}>
            <Select
              value={selectedValue} // Set value to the state variable
              onChange={handleChange} // Handle value change
              sx={{ width: '220px', height: '40px', fontSize: '0.9rem', opacity: 0.8 }}
            >
              <MenuItem value="1">Current Month</MenuItem>
              <MenuItem value="2">Previous Month</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* <Grid container spacing={2}> */}
      <Box sx={{ padding: 2, backgroundColor: "#fff", borderRadius: '12px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#F5F5F5',
              borderRadius: '12px',
              padding: 2,
              width: '327px',
              height: '62px',
              gap: 2,
            }}
          >
            <Box display="flex" alignItems="center">
              <Box component="img" src={AddProviderIcon} sx={{ width: '20px', height: '20px', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">Total Providers</Typography>
            </Box>
            <Typography variant="h6" sx={{ ml: 'auto', fontSize: '1.1rem', fontWeight: '500' }}>{metricsData?.response_data?.total_providers}</Typography>
          </Box>

          {/* Legend Section */}
          <Box display="flex" alignItems="center" gap={3}>
            <Box display="flex" alignItems="center" gap={0.8}>
              <Box sx={{ width: 12, height: 11, backgroundColor: '#CCE9F6', }} />
              <Typography variant="body2" color="text.secondary">Addition</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.8}>
              <Box sx={{ width: 12, height: 11, backgroundColor: '#d3d3d3', }} />
              <Typography variant="body2" color="text.secondary">Changes</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.8}>
              <Box sx={{ width: 12, height: 11, backgroundColor: '#f78f8f', }} />
              <Typography variant="body2" color="text.secondary">Termination</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ backgroundColor: '#F8F8F8', p: 1, borderRadius: '12px', height: '400px' }}>
          <Bar data={providerPayerData} options={options} />
        </Box>
      </Box>

      {/* <Grid item xs={12} md={4.5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 3,
                backgroundColor: '#fff',
                borderRadius: 2,
                height: '50%',
              }}
            >
              <Box display="flex" alignItems={"center"} >
                <Box component="img" src={newlyAdded} alt="Newly Joined Icon" sx={{ width: '28px', height: '28px', mr: 1 }} />
                <Typography variant="body2" color="text.secondary" align="center">Newly Joined Providers</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: "#F2FAFD", padding: 6, borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: '500' }} align="center">{metricsData.response_data?.new_joined_provider}</Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 3,
                backgroundColor: '#fff',
                borderRadius: 2,
                height: '50%',
              }}
            >
              <Box display="flex" alignItems={"center"}>
                <Box component="img" src={offBoarded} alt="Newly Joined Icon" sx={{ width: '28px', height: '28px', mr: 1 }} />
                <Typography variant="body2" color="text.secondary" align="center">Providers off-Boarded </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: "#FDF5F5", padding: 6, borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: '500' }} align="center">{metricsData.response_data?.offboaarding_providers}</Typography>
              </Box>
            </Box>
          </Box>
        </Grid> */}
      {/* </Grid> */}



      <Box mt={4} sx={{ backgroundColor: '#F8F8F8', borderRadius: '12px', padding: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>Time for Payers to Respond</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" mr={1}>Select Cycle</Typography>
            <FormControl variant="outlined" sx={{ minWidth: 150 }}>
              <Select
                value={selectedCycle}
                onChange={handleCycleChange}
                sx={{ width: '220px', height: '40px', fontSize: '0.9rem', opacity: 0.8 }}
              >
                <MenuItem value="1">Current Cycle</MenuItem>
                <MenuItem value="2">Previous Cycle</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ width: '100%', backgroundColor: '#fff', borderRadius: '12px', p: 0, height: '400px' }}>
          <Bar data={payerResponseData} options={payerResponseOptions} />
        </Box>
      </Box>

      <Grid container spacing={2} mt={4} >
        {/* Data Discrepancy from Payers */}
        <Grid item md={6} >
          <Box sx={{ backgroundColor: '#F8F8F8', borderRadius: '12px', padding: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>Data Discrepancy from Payers</Typography>
            <Box sx={{ display: 'flex', height: '400px', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff', borderRadius: '12px', padding: 2 }}>
              <Doughnut data={discrepancyData} options={discrepancyOptions} />
            </Box>
          </Box>
        </Grid>

        {/* Time to Resolve Discrepancies */}
        <Grid item xs={12} md={6}>
          <Box padding={2} sx={{ backgroundColor: '#F8F8F8', borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>Time to Resolve Discrepancies</Typography>
            <Box sx={{ display: 'flex', height: '400px', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff', borderRadius: '12px', padding: 2 }}>
              <Bar data={timeToResolveData} options={timeToResolveOptions} />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={4}>
        {/* Avg. Cycle Close Duration */}
        <Grid item xs={12}>
          <Box padding={2} sx={{ backgroundColor: '#F8F8F8', borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>Avg. Cycle Close Duration</Typography>
            <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', padding: 2, height: '400px' }}>
              <Bar data={cycleCloseDurationData} options={cycleCloseDurationOptions} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
